import { useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { useT } from '../lib/i18n/I18nContext';

/* ── Inline Logo (AC Nautitech compass rose) ────────────────────────────── */
// Exported so ResetPassword.jsx reuses the exact brand mark + card styling.
export const Logo = ({ size = 48 }) => (
  <svg width={size} height={size * 0.55} viewBox="0 0 100 55" fill="none">
    <path d="M5 48 L20 8 L35 48" stroke="var(--tm)" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M11 32 L29 32" stroke="var(--tm)" strokeWidth="5.5" strokeLinecap="round"/>
    <line x1="50" y1="2" x2="50" y2="50" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round"/>
    <ellipse cx="50" cy="5" rx="4" ry="4" stroke="var(--accent)" strokeWidth="2.5" fill="none"/>
    <ellipse cx="50" cy="5" rx="1.5" ry="1.5" fill="var(--accent)"/>
    <path d="M95 16 C88 10 72 10 65 22 C58 35 65 48 76 50 C84 51 90 47 95 42"
      stroke="var(--tm)" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
  </svg>
);

/* ── Styles ─────────────────────────────────────────────────────────────── */
export const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
    background: 'var(--bg-gradient, linear-gradient(145deg, #EEF2FF 0%, #F0F9FF 100%))',
    fontFamily: "-apple-system, 'SF Pro Display', 'Geist', sans-serif",
    WebkitFontSmoothing: 'antialiased',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    background: 'var(--bg-glass, rgba(255,255,255,0.65))',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    boxShadow: '0 1px 0 rgba(255,255,255,0.08) inset, 0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
    padding: 32,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  logoWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text-primary, #0F172A)',
    letterSpacing: '-0.01em',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: 400,
    color: 'var(--text-secondary, #58595B)',
    margin: 0,
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--text-secondary, #58595B)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    fontWeight: 400,
    fontFamily: 'inherit',
    color: 'var(--text-primary, #0F172A)',
    background: 'var(--bg-card, #FFFFFF)',
    border: '1px solid var(--border, rgba(0,0,0,0.06))',
    borderRadius: 8,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 200ms, box-shadow 200ms',
    minHeight: 44,
  },
  inputFocus: {
    borderColor: 'var(--accent)',
    boxShadow: '0 0 0 3px rgba(0,48,135,0.12)',
  },
  button: {
    width: '100%',
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: 'var(--card)',
    // HEX_TODO[#000000] exception — see color-map.json
    background: 'linear-gradient(135deg, #1C3F75 0%, #003087 100%)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    minHeight: 48,
    transition: 'opacity 200ms, transform 120ms',
    letterSpacing: '-0.01em',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--bad)',
    background: 'rgba(229,62,62,0.08)',
    borderRadius: 8,
    textAlign: 'center',
    margin: 0,
  },
  forgotLink: {
    fontSize: 13,
    color: 'var(--ac-blue, #003087)',
    textDecoration: 'none',
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'center',
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
    padding: 0,
  },
};

/* ── Component ──────────────────────────────────────────────────────────── */
export default function Login() {
  const { signIn, loading: authLoading } = useAuth();
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError(null);

    const { error: err } = await signIn(email.trim(), password);
    if (err) {
      setError(
        err.message === 'Invalid login credentials'
          ? t('auth.invalidCredentials')
          : err.message || t('auth.genericError')
      );
    }
    setSubmitting(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError(t('auth.enterEmailFirst'));
      return;
    }
    setSubmitting(true);
    setError(null);

    // Supabase sends password reset email
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (err) {
      setError(err.message);
    } else {
      setResetSent(true);
    }
    setSubmitting(false);
  };

  const disabled = submitting || authLoading;

  if (resetSent) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoWrap}>
            <Logo size={48} />
            <h1 style={styles.title}>{t('auth.checkEmail')}</h1>
          </div>
          <p style={{ ...styles.subtitle, textAlign: 'center', lineHeight: 1.5 }}>
            {t('auth.resetSent')} <strong>{email}</strong>. Follow the link to set a new password.
          </p>
          <button
            style={styles.button}
            onClick={() => { setResetSent(false); setShowReset(false); }}
          >
            {t('auth.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Logo + branding */}
        <div style={styles.logoWrap}>
          <Logo size={56} />
          <h1 style={styles.title}>{t('auth.brand')}</h1>
          <p style={styles.subtitle}>{t('auth.signInSubtitle')}</p>
        </div>

        {/* Error banner */}
        {error && <p style={styles.error}>{error}</p>}

        {/* Form */}
        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              disabled={disabled}
              style={{
                ...styles.input,
                ...(focusedField === 'email' ? styles.inputFocus : {}),
              }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={disabled}
              style={{
                ...styles.input,
                ...(focusedField === 'password' ? styles.inputFocus : {}),
              }}
            />
          </div>

          <button
            type="submit"
            disabled={disabled || !email || !password}
            style={{
              ...styles.button,
              ...(disabled || !email || !password ? styles.buttonDisabled : {}),
            }}
          >
            {submitting ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        {/* Forgot password */}
        <button
          style={styles.forgotLink}
          onClick={handleForgotPassword}
          disabled={disabled}
        >
          {t('auth.forgotPassword')}
        </button>
      </div>
    </div>
  );
}
